/**
 * Barra do indicativo em % da triagem. O número e o rótulo de tendência são // ux-neutrality:allow doc-comment
 * single-source do backend (a tendência é DERIVADA do % — lição F-E16.F2-01); // ux-neutrality:allow doc-comment
 * aqui só desenhamos. Apresentação humilde: "indícios de…", nunca conclusão // ux-neutrality:allow doc-comment
 * categórica. // ux-neutrality:allow doc-comment
 *
 * E100.F1 (GUS-64, ADR-0084): a régua tinha TRÊS zonas, com a do meio (40–60) // ux-neutrality:allow doc-comment
 * desenhando um resultado que não existe mais. Agora são duas, divididas no // ux-neutrality:allow doc-comment
 * corte de 50, e a agulha parte dali — a posição neutra era o centro da zona // ux-neutrality:allow doc-comment
 * que saiu, e o centro da régua é o mesmo ponto. // ux-neutrality:allow doc-comment
 *
 * E54.F6.T03 — a prancha 3A: card de blueprint com kicker, o percentual na // ux-neutrality:allow doc-comment
 * tipografia de título e a régua com a agulha correndo da posição neutra até // ux-neutrality:allow doc-comment
 * o valor. // ux-neutrality:allow doc-comment
 *
 * O percentual sai na face de título, e não em mono como todo dado pericial: é // ux-neutrality:allow doc-comment
 * o que a prancha 3A desenha, e o indicativo da triagem **não é dado pericial** // ux-neutrality:allow doc-comment
 * — nunca entra no laudo nem encosta no LR (ADR-0056). A escala da régua, essa // ux-neutrality:allow doc-comment
 * sim, é mono. Divergência registrada em docs/design/comparacoes/README.md. // ux-neutrality:allow doc-comment
 */
import { useEffect, useId, useState } from 'react';

import { Badge } from '@/components/ui/Badge';
import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import { ptBR } from '@/i18n/pt-BR';
import { CORTE_DA_TENDENCIA, type Tendencia } from '@/lib/pre_analise';
import { VEREDITO_ROTULO } from '@/lib/veredito';
import { TENDENCIA_LABEL } from './labels';

export interface IndicativeBarProps {
  indicativoPercentual: number;
  tendencia: Tendencia;
}

/** Cor de SINAL — resultado e direção, o único uso que o pacote permite. */
const _TENDENCIA_BADGE: Record<Tendencia, 'success' | 'danger'> = {
  compatibilidade: 'success',
  divergencia: 'danger',
};

/** A posição neutra de onde a agulha parte: o corte, que é o meio da régua. */
const _POSICAO_NEUTRA = CORTE_DA_TENDENCIA;

export function IndicativeBar({
  indicativoPercentual,
  tendencia,
}: IndicativeBarProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, indicativoPercentual));

  // Mesma regra de movimento da régua de suporte da mesa (E54.F5.T06): a agulha
  // entra no neutro e corre até o valor em `--sc-slow`. Sob
  // `prefers-reduced-motion` as durações do sistema já vão a 0ms na origem.
  const [agulhaArmada, setAgulhaArmada] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAgulhaArmada(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const posicaoAgulha = agulhaArmada ? clamped : _POSICAO_NEUTRA;
  // E67.F1.C02: a barra aparece uma vez por questionada — id fixo viraria N
  // elementos com o mesmo id na página do portão.
  const instancia = useId();
  const tituloId = `pre-analise-indicativo-title-${instancia}`;

  return (
    <Blueprint
      as="section"
      className="mt-sc-6 flex flex-col gap-sc-4 p-sc-6"
      aria-labelledby={tituloId}
    >
      <CardKicker id={tituloId}>{ptBR.preAnalise.indicativoSectionTitle}</CardKicker>

      <div className="flex flex-wrap items-baseline gap-x-sc-4 gap-y-2">
        <span
          className="font-display text-display-xl leading-none text-fg"
          data-testid="pre-analise-percentual"
        >
          {clamped}%
        </span>
        <Badge
          variant={_TENDENCIA_BADGE[tendencia]}
          className="ml-auto"
          data-testid="pre-analise-tendencia"
          data-tendencia={tendencia}
        >
          {TENDENCIA_LABEL[tendencia]}
        </Badge>
      </div>

      <div className="relative">
        <div
          className="flex h-3 w-full border border-border"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={clamped}
          aria-label={ptBR.preAnalise.indicativoMeterLabel}
          data-testid="pre-analise-bar"
        >
          {/* Duas zonas, nas tintas da rampa de sinal. A divisa vem da MESMA
              constante que o backend usa para derivar a tendência — a zona
              desenhada e o rótulo não podem discordar. */}
          <span
            className="block h-full bg-danger/30"
            style={{ width: `${CORTE_DA_TENDENCIA}%` }}
            aria-hidden="true"
          />
          <span className="block h-full flex-1 bg-success/30" aria-hidden="true" />
        </div>
        <div
          className="absolute -top-1 h-5 w-px -translate-x-1/2 bg-fg transition-[left] duration-slow ease-sc"
          style={{ left: `${posicaoAgulha}%` }}
          data-testid="pre-analise-marker"
          aria-hidden="true"
        >
          <span className="absolute -left-1 -top-1.5 block h-1.5 w-2 bg-fg [clip-path:polygon(50%_100%,0_0,100%_0)]" />
        </div>
      </div>

      <div className="flex" aria-hidden="true">
        <MonoData variant="label" style={{ width: `${CORTE_DA_TENDENCIA}%` }}>
          {VEREDITO_ROTULO.divergente}
        </MonoData>
        <MonoData variant="label" className="flex-1 text-right">
          {VEREDITO_ROTULO.convergente}
        </MonoData>
      </div>
    </Blueprint>
  );
}
